import type { FaqItem } from "@/components/site/FaqSection";

export const homeFaqs: FaqItem[] = [
  {
    question: "What is adaptED?",
    answer:
      "adaptED is a South African EdTech company building an AI learning companion for high school learners. It checks what a learner already knows, shows where the knowledge gaps are, and then helps close those gaps with practice and explanations aligned to the CAPS curriculum.",
  },
  {
    question: "Who is adaptED for?",
    answer:
      "Our current focus is Grade 10 to Grade 12 learners in South Africa working towards a bachelor pass. Parents and schools can follow the same learner journey through the parent and school portals.",
  },
  {
    question: "Is adaptED aligned to the CAPS curriculum?",
    answer:
      "Yes. adaptED is CAPS-native, which means the content, question styles and progression follow the South African CAPS curriculum rather than being adapted from a foreign syllabus. IEB, SACAI and other curriculums will be added soon.",
  },
  {
    question: "How much does adaptED cost?",
    answer:
      "adaptED is in its pilot phase, so pilot schools and their learners get early access at no cost while we build with them. Pricing will be shared with pilot schools before any paid plan launches.",
  },
  {
    question: "Do I need a laptop, or does it work on a phone?",
    answer:
      "adaptED is built mobile-first and runs in your phone browser, so a smartphone with a data connection is enough. It also works on tablets and laptops.",
  },
  {
    question: "How do I get access to adaptED?",
    answer:
      "The pilot programme starts in 2027. Learners and parents can get early access for the adaptED learning companion on the Join the Pilot page — no school nomination needed. Schools can also apply directly or be nominated by a parent or learner. In the meantime, try Quizzy, our free quiz tool, at www.quizzyonline.co.za.",
  },
];

export const productFaqs: FaqItem[] = [

  {
    question: "How does the adaptED learning companion work?",
    answer:
      "You pick a subject and topic, answer a short knowledge check by typing or speaking, and adaptED works out what you know. It then builds a personalised path: targeted explanations, practice questions and revision on the exact concepts you got wrong.",
  },
  {
    question: "What is knowledge-gap analysis?",
    answer:
      "Knowledge-gap analysis maps your answers to the underlying concepts in the curriculum, so instead of only a mark out of ten you see which specific ideas are missing and which earlier concepts to revisit first.",
  },
  {
    question: "What is Quizzy?",
    answer:
      "Quizzy is our free quiz product at www.quizzyonline.co.za. It lets learners test themselves on a topic quickly and is the fastest way to experience how adaptED checks knowledge before the full companion launches.",
  },
  {
    question: "Which subjects and grades are covered?",
    answer:
      "We are focused on Grade 10 to 12 CAPS subjects, starting with the subjects that most affect a bachelor pass, and we add subjects with input from teachers during the pilot. IEB, SACAI and other curriculums will be added soon.",
  },
  {
    question: "Is adaptED safe for learners to use?",
    answer:
      "Safety is built in by design. Responses are kept to curriculum-appropriate learning content, learner data is protected, and the companion is designed to support learners rather than simply hand over answers.",
  },
  {
    question: "Does adaptED replace teachers?",
    answer:
      "No. adaptED is built with educators, not around them. It handles repetitive practice, diagnosis and revision so teachers can spend their class time on the teaching only a person can do.",
  },
];

export const portalFaqs: FaqItem[] = [
  {
    question: "What are the three connected portals?",
    answer:
      "There is a learner portal for studying and practice, a parent portal for visibility and encouragement, and a school portal for teachers and school leaders. All three view the same learner journey from different angles.",
  },
  {
    question: "What can parents see in the parent portal?",
    answer:
      "Parents see progress, effort and where their child is struggling, in plain language, so they can support at home without needing to be an expert in the subject.",
  },
  {
    question: "What do schools get from the school portal?",
    answer:
      "Schools get a view of class and grade level performance, common knowledge gaps across learners, and reporting that helps teachers target intervention where it will make the most difference.",
  },
  {
    question: "Is learner data private?",
    answer:
      "Yes. Learners control their study experience, and parent and school views are limited to progress information relevant to supporting learning. We do not sell learner data.",
  },
  {
    question: "How do we get our school onto adaptED?",
    answer:
      "Register your school's interest through the school pilot form on the Join the Pilot page and our team will follow up to set up a pilot for your grades.",
  },
];

export const pilotFaqs: FaqItem[] = [
  {
    question: "What is the adaptED pilot?",
    answer:
      "The pilot, our Founding School Programme, commences in 2027. It is an exclusive programme where learners at participating schools get access to the adaptED learning companion plus hands-on support from the adaptED team, and shape the product with their feedback before the wider launch.",
  },
  {
    question: "Who can join the pilot?",
    answer:
      "Both individuals and schools can join. Learners and parents can get early access for the learning companion, and schools can apply to become part of the 2027 Founding School Programme. If you are a learner or parent, you can also nominate your school when you sign up.",
  },
  {
    question: "When does the pilot start?",

    answer:
      "The pilot programme commences in 2027. Schools that apply now are considered for the Founding School Programme and are contacted by our team as places open.",
  },
  {
    question: "What does a school need to provide when applying?",
    answer:
      "The school name and location (town/city and province), roughly how many Grade 12 learners it has, the biggest academic challenge it is facing, and contact details for the person applying — name, position, email address and contact number.",
  },
  {
    question: "Does it cost anything to join?",
    answer:
      "No. Applying and taking part in the pilot is free. We will always tell pilot schools about pricing before anything becomes paid.",
  },
  {
    question: "Do I need a school to sign up for early access?",
    answer:
      "No. Learners and parents can join the early access list on their own. You will also have the option to nominate your school if you would like it to be considered for the 2027 pilot.",
  },
  {
    question: "What happens after I sign up?",
    answer:
      "Individuals who join the early access list are added to the queue for the learning companion and are notified as spots open. Schools and nominated schools are contacted by our team to verify eligibility and arrange onboarding for 2027.",
  },
  {
    question: "Can I try something right now?",
    answer:
      "Yes. Quizzy at www.quizzyonline.co.za is live and free, so you can start checking your knowledge today while you wait for your adaptED invite.",
  },
];

export const pastPaperFaqs: FaqItem[] = [
  {
    question: "Where can I download Grade 12 past papers for free?",
    answer:
      "The Department of Basic Education publishes NSC past papers on its official website. Select your subject and year to download the question paper and memorandum. IEB papers are usually available through your school or the IEB portal.",
  },
  {
    question: "Are these past papers CAPS-aligned?",
    answer:
      "DBE NSC papers from 2014 onwards follow the CAPS curriculum, so they are the best match for current Grade 12 learners. Always check the year to make sure the paper covers the same exam guidelines you are preparing for.",
  },
  {
    question: "Which Grade 12 subjects have past papers available?",
    answer:
      "Mathematics, Mathematical Literacy, Physical Sciences, Life Sciences, Accounting, Business Studies, Economics, English Home Language, Afrikaans First Additional Language, History, Geography and Life Orientation are all available from the DBE archive.",
  },
  {
    question: "How many past papers should I do before matric finals?",
    answer:
      "Aim to complete at least three full papers per subject under exam conditions, then review every mistake. Quality matters more than quantity: one paper analysed properly teaches you more than five papers rushed through.",
  },
  {
    question: "Can adaptED help me prepare with past papers?",
    answer:
      "Yes. adaptED's knowledge-gap analysis shows you exactly which concepts caused your mistakes, so you study the right topics instead of repeating papers blindly. Join the early access list to be first to try it.",
  },
];

export const grade11PastPaperFaqs: FaqItem[] = [
  {
    question: "Where can I download Grade 11 past papers for free?",
    answer:
      "Grade 11 does not have national NSC exams, but the Department of Basic Education publishes exemplar papers and some provincial papers on its official website. Your school or provincial education department may also provide past Grade 11 exam papers — ask your teacher for the latest set.",
  },
  {
    question: "Are Grade 11 past papers CAPS-aligned?",
    answer:
      "DBE exemplar papers and most provincial Grade 11 papers follow the CAPS curriculum, so they are a good match for current learners. Always check the year to make sure the paper covers the same content you are studying.",
  },
  {
    question: "Which Grade 11 subjects have past papers available?",
    answer:
      "Mathematics, Mathematical Literacy, Physical Sciences, Life Sciences, Accounting, Business Studies, Economics, English Home Language, Afrikaans First Additional Language, History, Geography and Life Orientation typically have exemplar or provincial papers available.",
  },
  {
    question: "Why should I practise with Grade 11 past papers?",
    answer:
      "Grade 11 is your last full year of new content before matric. Practising with past papers now helps you master the fundamentals, identify knowledge gaps early, and build exam technique so you start Grade 12 already confident.",
  },
  {
    question: "Can adaptED help me prepare with Grade 11 past papers?",
    answer:
      "Yes. adaptED's knowledge-gap analysis shows you exactly which concepts caused your mistakes, so you fix the right topics before Grade 12 instead of repeating papers blindly. Join the early access list to be first to try it.",
  },
];

